import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tab,
  Tabs,
  Box,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImageIcon from "@mui/icons-material/Image";
import moment from "moment";
import ApiConfig from "../common/config/ApiConfig";
import { useTranslation } from "react-i18next";

const STATUS_COLOR = {
  pending: "warning",
  approve: "success",
  reject: "error",
};
const STATUS_KEY = {
  pending: "withdrawModal.status.pending",
  approve: "withdrawModal.status.approve",
  reject: "withdrawModal.status.reject",
};

function MemberWithdrawModal({ open, onClose, currency }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [debitInput, setDebitInput] = useState("");
  const [userId, setUserId] = useState(null);
  const [withdrawList, setWithdrawList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDebitValue, setEditDebitValue] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [rowError, setRowError] = useState({ id: null, message: "" });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const editFileRef = useRef(null);
  const user = localStorage.getItem("user");

  useEffect(() => {
    if (!open) return;
    ApiConfig.post("/profile", { user }).then((res) => {
      if (res.resCode === 200) setUserId(res.data.id);
    });
    fetchList();
    // eslint-disable-next-line
  }, [open]);

  // <img src> can't attach an Authorization header, and this endpoint is
  // auth-gated, so the file has to be fetched manually and turned into a
  // blob URL rather than referenced directly.
  useEffect(() => {
    if (!previewUrl) {
      setPreviewImageSrc(null);
      return;
    }
    let objectUrl;
    let cancelled = false;
    setPreviewLoading(true);
    fetch(previewUrl, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("Failed"))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewImageSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewImageSrc(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewUrl]);

  const fetchList = () => {
    setListLoading(true);
    setRowError({ id: null, message: "" });
    ApiConfig.post("/getmywithdraw", {}).then((res) => {
      if (res.resCode === 200) setWithdrawList(res.data);
      setListLoading(false);
    });
  };

  const handleSubmit = () => {
    const debit = parseFloat(debitInput);
    if (!debit || debit <= 0) {
      setFormError(t("withdrawModal.errors.amountRequired"));
      return;
    }
    if (!userId) {
      setFormError(t("withdrawModal.errors.userNotFound"));
      return;
    }
    setFormError("");
    setFormSuccess("");
    setSubmitting(true);
    const formData = new FormData();
    formData.append("requestBy", userId);
    formData.append("debit", debit);
    fetch(`${process.env.REACT_APP_API_KEY}/addmemberwithdraw`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: formData,
    })
      .then((r) => r.json())
      .then((res) => {
        setSubmitting(false);
        if (res.resCode === 200) {
          setDebitInput("");
          setFormSuccess(t("withdrawModal.messages.submitSuccess"));
          fetchList();
          setTab(1);
        } else {
          setFormError(res.message ?? t("withdrawModal.errors.generic"));
        }
      })
      .catch(() => {
        setSubmitting(false);
        setFormError(t("withdrawModal.errors.generic"));
      });
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditDebitValue(parseFloat(row.debit).toString());
    setEditFile(null);
    setRowError({ id: null, message: "" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDebitValue("");
    setEditFile(null);
    setRowError({ id: null, message: "" });
  };

  const handleSaveEdit = (id) => {
    const debit = parseFloat(editDebitValue);
    if (!debit || debit <= 0) return;
    setSavingId(id);
    const formData = new FormData();
    formData.append("id", id);
    formData.append("debit", debit);
    if (editFile) formData.append("fileEvidence", editFile);
    fetch(`${process.env.REACT_APP_API_KEY}/updatememberwithdraw`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: formData,
    })
      .then((r) => r.json())
      .then((res) => {
        setSavingId(null);
        if (res.resCode === 200) {
          setEditingId(null);
          setEditDebitValue("");
          setEditFile(null);
          fetchList();
        } else {
          setRowError({
            id,
            message: res.message ?? t("withdrawModal.errors.editFailed"),
          });
        }
      })
      .catch(() => {
        setSavingId(null);
        setRowError({ id, message: t("withdrawModal.errors.generic") });
      });
  };

  const handleDeleteConfirmed = () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    setDeletingId(id);
    ApiConfig.post("/deletememberwithdraw", { id }).then((res) => {
      setDeletingId(null);
      if (res.resCode === 200) {
        fetchList();
      } else {
        setRowError({
          id,
          message: res.message ?? t("withdrawModal.errors.deleteFailed"),
        });
      }
    });
  };

  const userWithdraws = withdrawList.filter(
    (t) => t.requestBy?.Username === user,
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className="bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-blue-500/40">
        <RemoveCircleIcon className="text-white" />
        <span className="text-white ml-2 font-bold">
          {t("withdrawModal.title")}
        </span>
      </DialogTitle>

      <Box className="bg-brand-blue-50">
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            "& .MuiTab-root.Mui-selected": { color: "#0d4bb3" },
            "& .MuiTabs-indicator": { backgroundColor: "#0d4bb3" },
          }}
        >
          <Tab
            icon={<RemoveCircleIcon />}
            iconPosition="start"
            label={t("withdrawModal.title")}
          />
          <Tab
            icon={<ListAltIcon />}
            iconPosition="start"
            label={t("withdrawModal.historyTab")}
          />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {tab === 0 && (
          <div className="flex flex-col space-y-4 p-2">
            <span className="text-brand-blue-800 font-bold text-base">
              {t("withdrawModal.amountPrompt")}
            </span>
            {formError && <Alert severity="error">{formError}</Alert>}
            {formSuccess && <Alert severity="success">{formSuccess}</Alert>}
            <TextField
              label={`${t("withdrawModal.amountLabel")}${currency ? ` (${currency.code})` : ""}`}
              type="number"
              value={debitInput}
              onChange={(e) => {
                setDebitInput(e.target.value);
                setFormError("");
                setFormSuccess("");
              }}
              fullWidth
              inputProps={{ min: 1, step: "any" }}
              variant="outlined"
            />
            <Button
              variant="contained"
              className="bg-gradient-to-b from-brand-blue-600 to-brand-blue-900 hover:from-brand-blue-500 hover:to-brand-blue-800"
              disabled={!debitInput || submitting || !userId}
              onClick={handleSubmit}
              startIcon={<RemoveCircleIcon />}
              sx={{
                color: "#ffd76b",
                "&:hover": { color: "#ffd76b" },
                "&.Mui-disabled": { color: "rgba(255,215,107,0.4)" },
              }}
            >
              {submitting
                ? t("withdrawModal.submitting")
                : t("withdrawModal.submitButton")}
            </Button>
          </div>
        )}

        {tab === 1 && (
          <div className="flex flex-col space-y-2">
            <div className="flex justify-end">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchList}
                disabled={listLoading}
              >
                {t("withdrawModal.refresh")}
              </Button>
            </div>
            {listLoading ? (
              <span className="text-center text-gray-500 py-6">
                {t("withdrawModal.loading")}
              </span>
            ) : userWithdraws.length === 0 ? (
              <span className="text-center text-gray-400 py-6">
                {t("withdrawModal.empty")}
              </span>
            ) : (
              <>
                <div className="hidden md:block">
                  <table className="table-auto w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-brand-blue-700 to-brand-blue-800 text-white">
                        <th className="px-3 py-2 text-left">
                          {t("withdrawModal.table.date")}
                        </th>
                        <th className="px-3 py-2 text-right">
                          {t("withdrawModal.table.amount")}
                        </th>
                        <th className="px-3 py-2 text-center">
                          {t("withdrawModal.table.status")}
                        </th>
                        <th className="px-3 py-2 text-left">
                          {t("withdrawModal.table.approver")}
                        </th>
                        <th className="px-3 py-2 text-center">
                          {t("withdrawModal.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userWithdraws.map((row, i) => (
                        <React.Fragment key={row.id}>
                          <tr
                            className={
                              i % 2 === 0 ? "bg-white" : "bg-brand-blue-50"
                            }
                          >
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                              {moment(row.createAt).format("DD/MM/YY HH:mm")}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-red-600">
                              {editingId === row.id ? (
                                <TextField
                                  type="number"
                                  value={editDebitValue}
                                  onChange={(e) =>
                                    setEditDebitValue(e.target.value)
                                  }
                                  size="small"
                                  inputProps={{ min: 1, step: "any" }}
                                  sx={{ width: 110 }}
                                />
                              ) : (
                                <>
                                  -
                                  {parseFloat(row.debit).toLocaleString(
                                    "th-TH",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}{" "}
                                  {currency?.symbol}
                                </>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Chip
                                label={
                                  STATUS_KEY[row.status]
                                    ? t(STATUS_KEY[row.status])
                                    : row.status
                                }
                                color={STATUS_COLOR[row.status] ?? "default"}
                                size="small"
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-500 text-xs">
                              {row.approveBy?.Username ?? "-"}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.status === "pending" &&
                                (editingId === row.id ? (
                                  <div className="flex flex-col items-center space-y-1">
                                    <div className="flex justify-center space-x-1">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        disabled={savingId === row.id}
                                        onClick={() => handleSaveEdit(row.id)}
                                      >
                                        <SaveIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={handleCancelEdit}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                    <label className="text-xs text-brand-blue-600 cursor-pointer hover:underline">
                                      <input
                                        ref={editFileRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) =>
                                          setEditFile(e.target.files[0] ?? null)
                                        }
                                      />
                                      {editFile
                                        ? editFile.name
                                        : t("withdrawModal.changeEvidence")}
                                    </label>
                                  </div>
                                ) : (
                                  <div className="flex justify-center space-x-1">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEdit(row)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={deletingId === row.id}
                                      onClick={() => setConfirmDeleteId(row.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                ))}
                            </td>
                          </tr>
                          {rowError.id === row.id && (
                            <tr>
                              <td colSpan={6} className="px-3 pb-2">
                                <Alert
                                  severity="error"
                                  onClose={() =>
                                    setRowError({ id: null, message: "" })
                                  }
                                >
                                  {rowError.message}
                                </Alert>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden flex flex-col space-y-2">
                  {userWithdraws.map((row) => (
                    <div
                      key={row.id}
                      className="rounded-lg border border-brand-blue-100 shadow-sm overflow-hidden bg-white"
                    >
                      <div className="flex items-center justify-between px-3 py-2 bg-brand-blue-50">
                        <span className="text-xs text-gray-500">
                          {moment(row.createAt).format("DD/MM/YY HH:mm")}
                        </span>
                        <Chip
                          label={
                            STATUS_KEY[row.status]
                              ? t(STATUS_KEY[row.status])
                              : row.status
                          }
                          color={STATUS_COLOR[row.status] ?? "default"}
                          size="small"
                        />
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">
                          {t("withdrawModal.table.amount")}
                        </span>
                        {editingId === row.id ? (
                          <TextField
                            type="number"
                            value={editDebitValue}
                            onChange={(e) => setEditDebitValue(e.target.value)}
                            size="small"
                            inputProps={{ min: 1, step: "any" }}
                            sx={{ width: 110 }}
                          />
                        ) : (
                          <span className="font-bold text-red-600">
                            -
                            {parseFloat(row.debit).toLocaleString("th-TH", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            {currency?.symbol}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">
                          {t("withdrawModal.table.approver")}
                        </span>
                        <span className="text-xs text-gray-700">
                          {row.approveBy?.Username ?? "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">
                          {t("withdrawModal.evidence")}
                        </span>
                        {row.fileEvidenceUrl ? (
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setPreviewUrl(row.fileEvidenceUrl)}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                      {row.status === "pending" && (
                        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
                          {editingId === row.id ? (
                            <div className="flex items-center justify-between">
                              <label className="text-xs text-brand-blue-600 cursor-pointer hover:underline">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) =>
                                    setEditFile(e.target.files[0] ?? null)
                                  }
                                />
                                {editFile
                                  ? editFile.name
                                  : t("withdrawModal.changeEvidence")}
                              </label>
                              <div className="flex space-x-1">
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={savingId === row.id}
                                  onClick={() => handleSaveEdit(row.id)}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-end space-x-1">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={deletingId === row.id}
                                onClick={() => setConfirmDeleteId(row.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </div>
                      )}
                      {rowError.id === row.id && (
                        <div className="px-3 pb-2 pt-1">
                          <Alert
                            severity="error"
                            onClose={() =>
                              setRowError({ id: null, message: "" })
                            }
                          >
                            {rowError.message}
                          </Alert>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#0d4bb3" }}>
          {t("withdrawModal.close")}
        </Button>
      </DialogActions>

      {/* Confirm delete dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="bg-red-600">
          <DeleteIcon className="text-white" />
          <span className="text-white ml-2 font-bold">
            {t("withdrawModal.confirmDelete.title")}
          </span>
        </DialogTitle>
        <DialogContent dividers>
          <span className="text-gray-700">
            {t("withdrawModal.confirmDelete.body")}
          </span>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteId(null)}
            sx={{ color: "#0d4bb3" }}
          >
            {t("withdrawModal.confirmDelete.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirmed}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t("withdrawModal.confirmDelete.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image preview lightbox */}
      <Dialog
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-blue-500/40">
          <ImageIcon className="text-white" />
          <span className="text-white ml-2 font-bold">
            {t("withdrawModal.evidencePreviewTitle")}
          </span>
        </DialogTitle>
        <DialogContent dividers className="flex justify-center p-2">
          {previewLoading ? (
            <span className="text-gray-500 py-6">
              {t("withdrawModal.loading")}
            </span>
          ) : (
            previewImageSrc && (
              <img
                src={previewImageSrc}
                alt="evidence"
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewUrl(null)} sx={{ color: "#0d4bb3" }}>
            {t("withdrawModal.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default MemberWithdrawModal;
